import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import type { ModalidadContacto, ObjetivoVisita, Visita } from '../services/visitas.service';

export const MAX_OBJETIVOS_VISITA_REGISTRO = 999;
export const ID_MODALIDAD_CONTACTO_PRESENCIAL = 1;

/** Normaliza valor de ion-select múltiple o legacy a ids numéricos únicos (máx. `max`). */
export function normalizarIdsObjetivoVisita(
  valor: number | number[] | string | string[] | null | undefined,
  max: number = MAX_OBJETIVOS_VISITA_REGISTRO
): number[] {
  if (valor == null || valor === '') return [];
  const arr = Array.isArray(valor) ? valor : [valor];
  const ids = arr
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(ids)].slice(0, max);
}

export function objetivosRegistroValidators(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const ids = normalizarIdsObjetivoVisita(control.value as number | number[] | null);
    if (ids.length === 0) return { required: true };
    return null;
  };
}

/** Agendar/reagendar: opcional; solo valida máximo de selección. */
export function objetivosAgendarValidators(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const ids = normalizarIdsObjetivoVisita(control.value as number | number[] | null);
    if (ids.length > MAX_OBJETIVOS_VISITA_REGISTRO) return { maxObjetivos: true };
    return null;
  };
}

/** OpenAPI CrearVisitaRequest / AgendarVisitaRequest: array JSON opcional. */
export function anexarObjetivosVisitaAPayloadJson<T extends Record<string, unknown>>(
  payload: T,
  ids: number[] | number | null | undefined,
  objetivo_otro?: string | null
): T & { id_objetivo_visita?: number[]; objetivo_otro?: string | null } {
  const normalizados = normalizarIdsObjetivoVisita(ids);
  const result: T & { id_objetivo_visita?: number[]; objetivo_otro?: string | null } = { ...payload };
  if (normalizados.length > 0) {
    result.id_objetivo_visita = normalizados;
  }
  const text = (objetivo_otro ?? '').trim();
  if (text) {
    result.objetivo_otro = text;
  }
  return result;
}

/** Deshabilita opciones no seleccionadas cuando ya se alcanzó el máximo (evita marcar un 4.º en el popover). */
export function esObjetivoVisitaOpcionDeshabilitada(
  id: number | string,
  idsSeleccionados: number[],
  max: number = MAX_OBJETIVOS_VISITA_REGISTRO
): boolean {
  if (idsSeleccionados.length < max) return false;
  const idNum = Number(id);
  return !idsSeleccionados.some((sel) => Number(sel) === idNum);
}

/** OpenAPI: en GET detalle `objetivo` es `string[]`; en listado puede ser texto único legacy. */
export function normalizarTextoModalidadDesdeApi(valor: unknown): string {
  if (valor == null) return '';
  if (typeof valor === 'string') return valor.trim();
  return String(valor).trim();
}

export function normalizarTextosObjetivoDesdeApi(
  valor: string | string[] | null | undefined
): string[] {
  if (valor == null) return [];
  if (Array.isArray(valor)) {
    return valor.map((v) => String(v ?? '').trim()).filter(Boolean);
  }
  const texto = String(valor).trim();
  if (!texto) return [];
  if (texto.includes(';') || texto.includes(',')) {
    return texto
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [texto];
}

export function extraerIdsObjetivoDesdeVisita(visita: Visita | null | undefined): number[] {
  if (!visita) return [];
  const raw =
    visita.id_objetivo_visita ?? visita.registro_visita?.id_objetivo_visita ?? null;
  return normalizarIdsObjetivoVisita(raw as number | number[] | null);
}

export function extraerIdModalidadDesdeVisita(visita: Visita | null | undefined): number | null {
  if (!visita) return null;
  const raw =
    visita.id_modalidad_contacto ?? visita.registro_visita?.id_modalidad_contacto ?? null;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatearObjetivosVisita(
  visita: Visita | null | undefined,
  catalogo?: ObjetivoVisita[]
): string {
  if (!visita) return '';
  // Prioriza el array `objetivos` normalizado (un tema por elemento, puede contener comas).
  const textosLista = [...new Set(normalizarTextosObjetivoDesdeApi(visita.objetivos ?? null))];
  if (textosLista.length) return textosLista.join('; ');
  // Fallback legacy: texto único en `objetivo` (separadores ; o ,) o registro.
  const textosDesdeString = [
    ...normalizarTextosObjetivoDesdeApi(visita.objetivo ?? null),
    ...normalizarTextosObjetivoDesdeApi(visita.registro_visita?.objetivo ?? null),
  ];
  const textosUnicos = [...new Set(textosDesdeString)];
  if (textosUnicos.length) return textosUnicos.join('; ');
  const ids = extraerIdsObjetivoDesdeVisita(visita);
  if (!ids.length || !catalogo?.length) return '';
  const descs = ids
    .map((id) => catalogo.find((o) => o.id_objetivo_visita === id)?.descripcion?.trim())
    .filter((d): d is string => !!d);
  return descs.join('; ');
}

/**
 * Alinea campos de registro al contrato OpenAPI (objetivo como string[] en GET detalle).
 * Debe aplicarse tras mapear cualquier respuesta de visita (listado o detalle).
 */
export function normalizarCamposRegistroEnVisita(visita: Visita): Visita {
  const objetivoRaw =
    visita.objetivo ?? visita.registro_visita?.objetivo ?? null;
  // Idempotente: si `objetivos` ya es un array normalizado (un tema por elemento),
  // no re-parsear el string `objetivo` (unido con '; '), que rompe temas con comas
  // al volver a partir por ','. Solo se parsean los textos si no hay array.
  const yaNormalizados = [
    ...new Set(normalizarTextosObjetivoDesdeApi(visita.objetivos ?? null)),
  ];
  const objetivos = yaNormalizados.length
    ? yaNormalizados
    : [
        ...new Set([
          ...normalizarTextosObjetivoDesdeApi(objetivoRaw),
          ...normalizarTextosObjetivoDesdeApi(visita.registro_visita?.objetivos ?? null),
        ]),
      ];
  const modalidad =
    normalizarTextoModalidadDesdeApi(visita.modalidad_contacto) ||
    normalizarTextoModalidadDesdeApi(visita.registro_visita?.modalidad_contacto);
  const idsObjetivo = extraerIdsObjetivoDesdeVisita(visita);

  return {
    ...visita,
    objetivos: objetivos.length ? objetivos : visita.objetivos ?? null,
    objetivo: objetivos.length ? objetivos.join('; ') : typeof visita.objetivo === 'string' ? visita.objetivo : null,
    modalidad_contacto: modalidad || visita.modalidad_contacto || null,
    id_objetivo_visita: idsObjetivo.length ? idsObjetivo : visita.id_objetivo_visita ?? null,
    registro_visita: visita.registro_visita
      ? {
          ...visita.registro_visita,
          objetivos: objetivos.length ? objetivos : visita.registro_visita.objetivos ?? null,
          objetivo: objetivos.length ? objetivos.join('; ') : visita.registro_visita.objetivo ?? null,
          modalidad_contacto: modalidad || (visita.registro_visita.modalidad_contacto ?? null),
        }
      : visita.registro_visita,
  };
}

export function formatearModalidadContacto(
  visita: Visita | null | undefined,
  catalogo?: ModalidadContacto[]
): string {
  if (!visita) return '';
  const texto =
    normalizarTextoModalidadDesdeApi(visita.modalidad_contacto) ||
    normalizarTextoModalidadDesdeApi(visita.registro_visita?.modalidad_contacto);
  if (texto) return texto;
  const id = extraerIdModalidadDesdeVisita(visita);
  if (id == null) return '';
  const found = catalogo?.find((m) => m.id_modalidad_contacto === id);
  return found?.descripcion?.trim() || String(id);
}

export function appendObjetivosVisitaAFormData(formData: FormData, ids: number[], objetivo_otro?: string | null): void {
  for (const id of normalizarIdsObjetivoVisita(ids)) {
    formData.append('id_objetivo_visita', String(id));
  }
  const text = (objetivo_otro ?? '').trim();
  if (text) {
    formData.append('objetivo_otro', text);
  }
}

/** OpenAPI: `generar_folio` en listado, detalle y POST /registrar (multipart). */
export function extraerGenerarFolioDesdeVisita(visita: Visita | null | undefined): boolean {
  if (!visita) return false;
  // La raíz del GET /visita/{id} prevalece (incluye `false` explícito del servidor).
  if (typeof visita.generar_folio === 'boolean') {
    return visita.generar_folio;
  }
  return visita.registro_visita?.generar_folio === true;
}

/** Prioriza el valor booleano explícito del detalle GET sobre el listado u objeto anidado. */
export function resolverGenerarFolioVisita(
  detalle?: Visita | null,
  origen?: Visita | null
): boolean {
  if (typeof detalle?.generar_folio === 'boolean') {
    return detalle.generar_folio;
  }
  if (typeof origen?.generar_folio === 'boolean') {
    return origen.generar_folio;
  }
  const regDetalle = detalle?.registro_visita;
  const regOrigen = origen?.registro_visita;
  if (typeof regDetalle?.generar_folio === 'boolean') {
    return regDetalle.generar_folio;
  }
  if (typeof regOrigen?.generar_folio === 'boolean') {
    return regOrigen.generar_folio;
  }
  return false;
}

export function appendGenerarFolioAFormData(formData: FormData, generar: boolean): void {
  formData.append('generar_folio', generar ? 'true' : 'false');
}
